import { CheckCircle, Mail, MapPin, Phone, Send } from "lucide-react";
import { useState } from "react";
import { CONTACT, SITE_CONFIG } from "../../data/mock";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    school: "",
    message: "",
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setIsSubmitted(true);
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", school: "", message: "" });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
    },
    {
      icon: Phone,
      label: "Phone",
      value: CONTACT.phone,
      href: `tel:${CONTACT.phone.replace(/\s/g, "")}`,
    },
    {
      icon: MapPin,
      label: "Location",
      value: CONTACT.address,
      href: null,
    },
  ];

  return (
    <section
      id="contact"
      aria-labelledby="contact-heading"
      aria-describedby="contact-description"
      className="py-24 bg-[var(--bg-section)] transition-theme"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
            Contact Us
          </span>
          <h2
            id="contact-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[var(--text-primary)] mb-6 leading-tight"
          >
            Let's Get Your School Started
          </h2>
          <p
            id="contact-description"
            className="text-lg text-[var(--text-body)] opacity-90"
          >
            Ready to transform your school administration? Get in touch with us
            today.
          </p>
        </div>

        {/* Contact Info - Horizontal Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
          {contactInfo.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center p-6 rounded-2xl bg-[var(--bg-card)] border border-[var(--border-light)] hover:border-[var(--accent-primary)]/30 transition-all duration-300"
            >
              <div className="w-14 h-14 rounded-xl bg-[var(--accent-wash)] flex items-center justify-center mb-4">
                <item.icon className="w-6 h-6 text-[var(--accent-text)]" />
              </div>
              <p className="text-sm text-[var(--text-secondary)] font-medium mb-1">
                {item.label}
              </p>
              {item.href ? (
                <a
                  href={item.href}
                  className="text-[var(--text-primary)] font-medium hover:text-[var(--accent-text)] transition-colors"
                >
                  {item.value}
                </a>
              ) : (
                <p className="text-[var(--text-primary)] font-medium">
                  {item.value}
                </p>
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Left Column - Get in Touch + CTA */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
                Get in Touch
              </h3>
              <p className="text-[var(--text-secondary)] leading-relaxed">
                Whether you have a question about features, pricing, or anything
                else, our team is ready to answer all your questions.
              </p>
            </div>

            {/* Quick Start CTA */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-[var(--accent-wash)] to-transparent border border-[var(--accent-primary)]/20">
              <h4 className="font-semibold text-[var(--text-primary)] mb-2">
                Ready to get started?
              </h4>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                Sign up now and start managing your school in minutes.
              </p>
              <a
                href={SITE_CONFIG.signupFormUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="btn-gradient text-white hover:opacity-90 rounded-full">
                  Get Started Free
                </Button>
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-[var(--bg-card)] p-8 rounded-2xl border border-[var(--border-light)] shadow-lg">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[var(--accent-wash)] flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-[var(--accent-text)]" />
                </div>
                <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                  Message Sent!
                </h3>
                <p className="text-[var(--text-secondary)]">
                  We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    required
                    className="rounded-xl border-[var(--border-light)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@school.edu"
                    required
                    className="rounded-xl border-[var(--border-light)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    School Name
                  </label>
                  <Input
                    type="text"
                    name="school"
                    value={formData.school}
                    onChange={handleChange}
                    placeholder="ABC Primary School"
                    className="rounded-xl border-[var(--border-light)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your school and what you're looking for..."
                    rows={4}
                    required
                    className="rounded-xl border-[var(--border-light)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]/20 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full btn-gradient text-white hover:opacity-90 rounded-full py-6 font-semibold"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
